import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';

interface InitialsAvatarProps {
  name?: string;
  size?: number;
  className?: string;
  /**
   * When set, the avatar renders this image (a presigned photo URL) instead of
   * the initials. If the image fails to load, it silently falls back to the
   * initials — so a stale/expired URL never shows a broken-image icon.
   */
  photoUrl?: string | null;
}

export function InitialsAvatar({ name = '?', size = 32, className, photoUrl }: InitialsAvatarProps) {
  const parts = name.trim().split(/\s+/);
  const initials = parts
    .map((p) => p[0] ?? '')
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const hue =
    [...name].reduce((acc, ch) => acc + ch.charCodeAt(0), 0) % 360;

  // Reset the error state when the URL changes so a new photo gets a fresh try.
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [photoUrl]);

  if (photoUrl && !failed) {
    return (
      <img
        src={photoUrl}
        alt={name}
        width={size}
        height={size}
        onError={() => setFailed(true)}
        className={cn('inline-block shrink-0 rounded-full object-cover', className)}
        style={{ width: size, height: size }}
      />
    );
  }

  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center justify-center rounded-full font-semibold',
        className,
      )}
      style={{
        width: size,
        height: size,
        fontSize: size * 0.38,
        background: `oklch(0.93 0.025 ${hue})`,
        color: `oklch(0.35 0.08 ${hue})`,
      }}
      aria-hidden="true"
    >
      {initials || '?'}
    </span>
  );
}
